import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {orgId, defaultErrorHandler} from '../../../context/context';
import shopifyClientV1 from '../../../infrastructure/acerill/shopify-v1-client';
import shopifyV1Client from '../../../infrastructure/acerill/shopify-v1-client';
import customFormsV2Client from '../../../infrastructure/acerill/custom-forms-v2-client';
import authenticationService from '../../../services/authentication-service';
import Section from '../../../libs/react-components-admin/section/section';
import SubSection from '../../../libs/react-components-admin/section/sub-section';
import ActionButton from '../../../libs/react-components-admin/buttons/action-button';
import CenteredSpinner from '../../../libs/react-components-admin/spinners/centered-spinner';
import NamedIntegrations from '../../../libs/react-components-admin/integration/named-integrations';
import InfoBox from '../../../libs/react-components-admin/box/info-box';
import IntegrationOnlyForStandalone from '../common/integration-only-for-standalone';

const allAccessScopes = [
  // {key: 'edit-content', label: 'Edit content (pages, blogs, blog posts)', readScope: 'read_content', writeScope: 'write_content'},
  {key: 'edit-customers', label: 'Edit customers', readScope: 'read_customers', writeScope: 'write_customers'},
  {key: 'edit-orders', label: 'Edit orders', readScope: 'read_orders', writeScope: 'write_orders'},
  {key: 'edit-products', label: 'Edit products and collections', readScope: 'read_products', writeScope: 'write_products'},
];

const Permissions = ({grantedAccessScopes}) => {

  const [selectedScopeKeys, setSelectedScopeKeys] = useState([]);

  const alreadyGranted = scope => grantedAccessScopes.includes(scope.readScope) &&
    grantedAccessScopes.includes(scope.writeScope);

  const updateSelectedScopes = (scopeKey, selected) => {
    if (selected) {
      setSelectedScopeKeys([...selectedScopeKeys, scopeKey]);
    } else {
      setSelectedScopeKeys(selectedScopeKeys.filter(k => k !== scopeKey));
    }
  };

  if (!allAccessScopes.some(s => !alreadyGranted(s))) {
    return <></>;
  }
  return <SubSection title="Permissions" className="mb-4">
    <div className="text-muted mb-2">
      Some integrations below require extra permissions to be granted to the app.<br/>
      Use this section to grant these extra permissions.
    </div>
    <ul className="list-group">
      {allAccessScopes.map(scope => <li key={scope.key} className="list-group-item">
        <div className="row">
          <div className="col">{scope.label}</div>
          <div className="col-auto">
            <div className="form-check">
              <input type="checkbox"
                     id={'access-scope-' + scope.key}
                     className="form-check-input"
                     checked={alreadyGranted(scope) || selectedScopeKeys.includes(scope.key)}
                     onChange={e => updateSelectedScopes(scope.key, e.target.checked)}
                     disabled={alreadyGranted(scope)}/>
            </div>
          </div>
        </div>
      </li>)}
      <li className="list-group-item">
        <div className="row justify-content-end">
          <div className="col-auto">
            <ActionButton className="btn-primary"
                          action={() => authenticationService.redirectToLoginUrl(
                            'integrations_shopify',
                            selectedScopeKeys.reduce((result, scopeKey) => {
                              const scope = allAccessScopes.find(s => s.key === scopeKey);
                              result.push(scope.readScope);
                              result.push(scope.writeScope);
                              return result;
                            }, [])
                          )}
                          disabled={selectedScopeKeys.length === 0}>
              Grant selected permissions
            </ActionButton>
          </div>
        </div>
      </li>
    </ul>
  </SubSection>;
};

const SaveMetafieldsIntegrations = ({grantedAccessScopes, initialIntegrations}) => {

  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState(initialIntegrations);

  const alreadyGranted = scope => grantedAccessScopes.includes(scope.readScope) &&
    grantedAccessScopes.includes(scope.writeScope);

  const goToIntegration = id => {
    navigate(`/integrations/shopify/save-metafields/${encodeURIComponent(id)}?shop=${encodeURIComponent(orgId)}`);
    return Promise.resolve(null);
  };

  const deleteIntegration = id => {
    return customFormsV2Client
      .deleteShopifyIntegration(id)
      .then(() => setIntegrations(integrations.filter(i => i.id !== id)));
  };

  if (!allAccessScopes.some(s => alreadyGranted(s))) {
    return <InfoBox>
      <div className="row justify-content-center">
        <div className="col-auto">
          Please grant at least one permission (see top of this page).
        </div>
      </div>
    </InfoBox>;
  }
  return <NamedIntegrations integrations={integrations}
                            createAction={() => goToIntegration(uuidv4())}
                            editAction={id => goToIntegration(id)}
                            deleteAction={id => deleteIntegration(id)}
                            errorHandler={defaultErrorHandler}/>;
};

const SaveCustomerIntegrations = ({grantedAccessScopes, initialIntegrations}) => {

  const navigate = useNavigate();

  const [integrations, setIntegrations] = useState(initialIntegrations);

  const goToIntegration = id => {
    navigate(`/integrations/shopify/save-customer/${encodeURIComponent(id)}?shop=${encodeURIComponent(orgId)}`);
    return Promise.resolve(null);
  };

  const deleteIntegration = id => {
    return customFormsV2Client
      .deleteShopifyIntegration(id)
      .then(() => setIntegrations(integrations.filter(i => i.id !== id)));
  };

  if (!grantedAccessScopes.includes('read_customers') || !grantedAccessScopes.includes('write_customers')) {
    return <InfoBox>
      <div className="row justify-content-center">
        <div className="col-auto">
          Please grant the permission &apos;Edit customers&apos; (see top of this page).
        </div>
      </div>
    </InfoBox>;
  }
  return <NamedIntegrations integrations={integrations}
                            createAction={() => goToIntegration(uuidv4())}
                            editAction={id => goToIntegration(id)}
                            deleteAction={id => deleteIntegration(id)}
                            errorHandler={defaultErrorHandler}/>;
};

const ShopifyIntegrations = () => {

  const [grantedAccessScopes, setGrantedAccessScopes] = useState();
  const [saveMetafieldsIntegrations, setSaveMetafieldsIntegrations] = useState();
  const [saveCustomerIntegrations, setSaveCustomerIntegrations] = useState();

  useEffect(() => {
    Promise
      .all([
        shopifyClientV1.findAccessScopes(),
        shopifyV1Client.findIntegrations(),
      ])
      .then(([accessScopes, integrations]) => {
        setGrantedAccessScopes(accessScopes);
        const filterAndSortIntegrations = type => integrations
          .filter(i => i.type === type)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSaveMetafieldsIntegrations(filterAndSortIntegrations('save-metafields'));
        setSaveCustomerIntegrations(filterAndSortIntegrations('save-customer'));
      })
      .catch(defaultErrorHandler);
  }, []);

  return <Section title="Integration - Shopify" noMargin={true}>
    {(!grantedAccessScopes || !saveMetafieldsIntegrations || !saveCustomerIntegrations) ?
      <CenteredSpinner/> :
      <div className="row justify-content-center">
        <div className="col-12 col-md-8">
          <IntegrationOnlyForStandalone/>
          <Permissions grantedAccessScopes={grantedAccessScopes}/>
          <SubSection title="Create / update metafields">
            <div className="text-muted mb-2">
              Creates or updates a list of metafields when a form is submitted.
            </div>
            <div className="mb-4">
              <SaveMetafieldsIntegrations grantedAccessScopes={grantedAccessScopes}
                                          initialIntegrations={saveMetafieldsIntegrations}/>
            </div>
          </SubSection>
          <SubSection title="Create / update customers">
            <div className="text-muted mb-2">
              Creates or updates a Shopify customer when a form is submitted.<br/>
              When the form is submitted by a customer who is logged in, an update to that customer happens.
            </div>
            <div className="mb-4">
              <SaveCustomerIntegrations grantedAccessScopes={grantedAccessScopes}
                                        initialIntegrations={saveCustomerIntegrations}/>
            </div>
          </SubSection>
        </div>
      </div>
    }
  </Section>
};

export default ShopifyIntegrations;
